import { useNavigate } from 'react-router-dom';

const usefulLinksConfig = (navigate) => [
  {
    title: 'Student Portal',
    description: 'Sign in to access your classes, assignments, grades, and attendance.',
    href: () => navigate('/login'),
    cta: 'Sign In',
    primary: true,
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v3.75M15 12l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    ),
  },
  {
    title: 'Curriculum',
    description: 'Two-year Islamic sciences program: objectives, teaching method, and course overview.',
    href: () => navigate('/curriculum'),
    cta: 'View Curriculum',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  {
    title: 'Academic Calendar',
    description: 'View the full academic calendar and class schedules.',
    href: () => navigate('/calendar'),
    cta: 'View Calendar',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
      </svg>
    ),
  },
  {
    title: 'Textbooks',
    description: 'Required and recommended textbooks with purchase links.',
    href: () => navigate('/textbooks'),
    cta: 'View Textbooks',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  {
    title: 'Parent Lookup',
    description: 'View your child\'s progress, grades, and attendance.',
    href: () => navigate('/parent'),
    cta: 'Parent Access',
    icon: (
      <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
      </svg>
    ),
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const usefulLinks = usefulLinksConfig(navigate);

  return (
    <div className="elegant-font min-h-screen bg-white text-neutral-900">
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-neutral-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.jpeg" alt="Abubakar Islamic Institute" className="h-9 sm:h-10 w-auto object-contain" />
              <span className="font-elegant-display text-lg sm:text-xl font-semibold text-neutral-900 tracking-tight">
                Abubakar Islamic Institute
              </span>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="font-elegant text-sm font-medium text-primary-600 hover:text-primary-700 px-4 py-2 rounded-lg hover:bg-primary-50 transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="relative pt-28 pb-20 sm:pt-36 sm:pb-24 px-4 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-neutral-50 to-white" aria-hidden="true" />
          <div className="relative max-w-4xl mx-auto text-center">
            <h1 className="font-elegant-display text-4xl sm:text-5xl md:text-6xl font-semibold text-neutral-900 tracking-tight leading-tight mb-4">
              Abubakar Islamic Institute
            </h1>
            <p className="font-elegant text-xl sm:text-2xl text-neutral-600 mb-4">
              A Two-Year Islamic Sciences Program
            </p>
            <p className="font-elegant text-neutral-500 max-w-xl mx-auto">
              Explore our curriculum, academic calendar, textbooks, and sign in to the student portal below.
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="py-16 sm:py-20 px-4 bg-neutral-50/80">
          <div className="max-w-4xl mx-auto">
            <h2 className="font-elegant-display text-2xl sm:text-3xl font-semibold text-neutral-900 text-center mb-10 tracking-tight">
              Mission & Vision
            </h2>
            <div className="grid sm:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-neutral-200/60">
                <h3 className="font-elegant-display text-lg font-semibold text-neutral-900 mb-3">Mission</h3>
                <p className="font-elegant text-neutral-700 leading-relaxed">
                  Raise confident Muslims. Produce well rounded Muslims.
                </p>
              </div>
              <div className="bg-white rounded-2xl p-8 shadow-soft border border-neutral-200/60">
                <h3 className="font-elegant-display text-lg font-semibold text-neutral-900 mb-3">Vision</h3>
                <p className="font-elegant text-neutral-700 leading-relaxed">
                  Make Islam relevant in an ever-changing world.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Curriculum preview */}
        <section className="py-12 sm:py-16 px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-elegant-display text-2xl font-semibold text-neutral-900 text-center mb-6 tracking-tight">
              Curriculum
            </h2>
            <p className="font-elegant text-neutral-600 text-center mb-8 leading-relaxed">
              A two-year program across six levels: Aqeeda, Fiqh of Worship, Tazkiya & Ethics, Tafseer, Seerah, Hadith, Arabic, and more.
            </p>
            <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8">
              {['Level I', 'Level II', 'Level III', 'Level IV', 'Level V', 'Level VI'].map((level) => (
                <span
                  key={level}
                  className="font-elegant text-sm px-4 py-2 rounded-full bg-neutral-100 text-neutral-700 border border-neutral-200/80"
                >
                  {level}
                </span>
              ))}
            </div>
            <div className="text-center">
              <button
                onClick={() => navigate('/curriculum')}
                className="font-elegant font-medium text-primary-600 hover:text-primary-700 inline-flex items-center gap-2 group"
              >
                Learn more
                <svg className="w-5 h-5 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </section>

        {/* Useful Links */}
        <section className="py-16 sm:py-24 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-elegant-display text-2xl sm:text-3xl font-semibold text-neutral-900 text-center mb-4 tracking-tight">
              Useful Links
            </h2>
            <p className="font-elegant text-neutral-600 text-center max-w-xl mx-auto mb-12">
              Curriculum, academic calendar, textbooks, parent access, and the student portal.
            </p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {usefulLinks.map((link) => (
                <button
                  key={link.title}
                  type="button"
                  onClick={link.href}
                  className={`group text-left rounded-2xl p-6 sm:p-8 border transition-all duration-200 hover:shadow-medium hover:-translate-y-1 flex flex-col ${
                    link.primary ? 'bg-primary-600 border-primary-600 text-white hover:bg-primary-700 hover:border-primary-700' : 'bg-white border-neutral-200/80 hover:border-primary-200'
                  }`}
                >
                  <span className={`mb-4 ${link.primary ? 'text-white/90' : 'text-primary-600'}`}>{link.icon}</span>
                  <h3 className={`font-elegant-display text-lg font-semibold mb-2 tracking-tight ${link.primary ? 'text-white' : 'text-neutral-900'}`}>{link.title}</h3>
                  <p className={`font-elegant text-sm flex-1 ${link.primary ? 'text-white/80' : 'text-neutral-600'} leading-relaxed`}>{link.description}</p>
                  <span className={`font-elegant text-sm font-medium mt-4 inline-flex items-center gap-1 ${link.primary ? 'text-white' : 'text-primary-600 group-hover:underline'}`}>
                    {link.cta}
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-neutral-200 bg-neutral-100 py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="font-elegant text-sm text-neutral-700 space-y-1 text-center sm:text-left">
              <p>
                <a href="https://www.google.com/maps/search/?api=1&query=2824+13th+Ave+S+Minneapolis+MN+55407" target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700 hover:underline">
                  2824 13th Ave S, Minneapolis, MN 55407
                </a>
              </p>
              <p>
                <a href="tel:+16128718600" className="text-primary-600 hover:text-primary-700 hover:underline">(612) 871-8600</a>
              </p>
              <p className="text-neutral-600">Open 9–12:30 Sat–Sun</p>
          </div>
          <p className="font-elegant text-sm text-neutral-500 mt-8 pt-6 border-t border-neutral-200 text-center">
            © {new Date().getFullYear()} Abubakar Islamic Institute. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
